import { useState, useMemo, useEffect } from 'react';
import { XMarkIcon, UserPlusIcon, MagnifyingGlassIcon, CheckIcon, LockClosedIcon, LockOpenIcon } from '@heroicons/react/24/outline';
import type { Flow, Etapa, Colaborador, PrazoProcesso } from '../../types';
import { etapasDePausa } from '../../utils/prazoProcesso';
import { apiUrl } from '../../utils/apiBase';

const AVATAR_COLORS = [
  'bg-indigo-500', 'bg-purple-500', 'bg-pink-500', 'bg-blue-500',
  'bg-emerald-500', 'bg-orange-500', 'bg-teal-500', 'bg-rose-500',
];

const getInitials = (nome: string) => {
  const parts = nome.trim().split(/\s+/);
  return parts.length > 1
    ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    : (nome.substring(0, 2)).toUpperCase();
};

interface PermissoesFluxo {
  colaboradorId: string;
  permissoesGerais: {
    criarCards: {
      permitido: boolean;
      etapas?: string[]; // IDs das etapas onde pode criar
    };
    editarCards: {
      permitido: boolean;
      etapas?: string[]; // IDs das etapas onde pode editar
      campos?: string[]; // IDs dos campos que pode editar
    };
    deletarCards: {
      permitido: boolean;
      etapas?: string[]; // IDs das etapas onde pode deletar
    };
    moverCards: {
      permitido: boolean;
      regras?: Array<{
        deEtapaId: string;
        paraEtapasIds: string[];
      }>;
    };
    transformarEmPai: {
      permitido: boolean;
      etapas?: string[]; // IDs das etapas onde pode transformar
    };
    configurarEtapas: {
      permitido: boolean;
      etapas?: string[];   // IDs das etapas que pode configurar (vazio = todas)
      secoes?: string[];   // quais abas pode configurar (vazio = todas)
    };
    gerenciarPortal?: {
      visualizar: boolean;
      editar: boolean;
    };
  };
  permissoesPorEtapa: {
    [etapaId: string]: {
      acessar: boolean;
      moverPara: boolean;
      moverDe: boolean;
      escopoCards?: 'todos' | 'proprios' | 'responsavel' | 'mencionado';
    };
  };
  permissoesPorCampo: {
    [campoId: string]: {
      visualizar: boolean;
      editar: boolean;
    };
  };
}

interface ModalConfigPermissoesProps {
  flow: Flow;
  colaboradores: Colaborador[];
  onClose: () => void;
  onSave: (
    permissoes: PermissoesFluxo[],
    cotacao?: { habilitado: boolean; rotulo?: string },
    prazoProcesso?: PrazoProcesso
  ) => void;
}

const ModalConfigPermissoes = ({ flow, colaboradores, onClose, onSave }: ModalConfigPermissoesProps) => {
  const [colaboradorSelecionado, setColaboradorSelecionado] = useState<string | null>(null);
  const [pesquisa, setPesquisa] = useState('');
  const [permissoes, setPermissoes] = useState<PermissoesFluxo[]>([]);
  const [permissaoExpandida, setPermissaoExpandida] = useState<string | null>(null);

  // 💰 Cotação com fornecedor: vale para o fluxo inteiro, não etapa a etapa
  const [cotacaoHabilitada, setCotacaoHabilitada] = useState<boolean>(flow.configuracaoCotacao?.habilitado ?? false);
  const [cotacaoRotulo, setCotacaoRotulo] = useState<string>(flow.configuracaoCotacao?.rotulo || '');

  // ⏱️ Prazo do processo: um relógio só para o card inteiro, do fluxo todo
  const [prazoHabilitado, setPrazoHabilitado] = useState<boolean>(flow.prazoProcesso?.habilitado ?? false);
  const [prazoHoras, setPrazoHoras] = useState<string>(String(flow.prazoProcesso?.horas ?? 24));
  const [prazoIgnorarFds, setPrazoIgnorarFds] = useState<boolean>(flow.prazoProcesso?.ignorarFimDeSemana ?? false);
  const [prazoPausa, setPrazoPausa] = useState<string[]>(() => Array.from(etapasDePausa(flow)));

  // Carregar permissões existentes do backend
  useEffect(() => {
    console.log('📥 Carregando permissões do servidor...');
    fetch(apiUrl(`/flows/${flow.id}/permissoes`))
      .then(res => res.json())
      .then(data => {
        if (data.permissoes && Array.isArray(data.permissoes)) {
          console.log('✅ Permissões carregadas:', data.permissoes);
          setPermissoes(data.permissoes);
        } else {
          console.log('ℹ️ Nenhuma permissão encontrada, iniciando com array vazio');
        }
      })
      .catch(err => {
        console.error('❌ Erro ao carregar permissões:', err);
      });
  }, [flow.id]);

  // Colaboradores filtrados pela pesquisa
  const colaboradoresFiltrados = useMemo(() => {
    if (!pesquisa) return colaboradores;
    const termo = pesquisa.toLowerCase();
    return colaboradores.filter(c => 
      c.nome?.toLowerCase().includes(termo) || 
      c.email?.toLowerCase().includes(termo)
    );
  }, [colaboradores, pesquisa]);

  // Permissões do colaborador selecionado
  const permissoesColaborador = useMemo(() => {
    if (!colaboradorSelecionado) return null;
    return permissoes.find(p => p.colaboradorId === colaboradorSelecionado) || {
      colaboradorId: colaboradorSelecionado,
      permissoesGerais: {
        criarCards: { permitido: true, etapas: [] },
        editarCards: { permitido: true, etapas: [], campos: [] },
        deletarCards: { permitido: false, etapas: [] },
        // 🔒 Movimentação manual entre etapas nasce BLOQUEADA: só quem recebe
        // o toggle explicitamente é que arrasta card de uma etapa para outra.
        moverCards: { permitido: false, regras: [] },
        transformarEmPai: { permitido: false, etapas: [] },
        configurarEtapas: { permitido: false, etapas: [], secoes: [] },
        gerenciarPortal: { visualizar: false, editar: false },
      },
      permissoesPorEtapa: {},
      permissoesPorCampo: {},
    };
  }, [colaboradorSelecionado, permissoes]);

  // Atualizar permissões gerais (toggle principal)
  const togglePermissaoGeral = (chave: keyof PermissoesFluxo['permissoesGerais']) => {
    if (!colaboradorSelecionado || !permissoesColaborador) return;
    
    console.log('🔄 Toggle permissão geral:', chave);
    
    const novasPermissoes = [...permissoes];
    const index = novasPermissoes.findIndex(p => p.colaboradorId === colaboradorSelecionado);
    
    const permissaoAtual = permissoesColaborador.permissoesGerais[chave];
    let novoValor: any;
    
    novoValor = {
      ...(permissaoAtual as any),
      permitido: !(permissaoAtual as any).permitido,
    };
    
    const permissaoAtualizada = {
      ...permissoesColaborador,
      permissoesGerais: {
        ...permissoesColaborador.permissoesGerais,
        [chave]: novoValor,
      },
    };
    
    if (index >= 0) {
      novasPermissoes[index] = permissaoAtualizada;
    } else {
      novasPermissoes.push(permissaoAtualizada);
    }
    
    setPermissoes(novasPermissoes);
  };

  // Adicionar/remover etapa da permissão de configurar etapas
  const toggleEtapaConfigurar = (etapaId: string) => {
    if (!colaboradorSelecionado || !permissoesColaborador) return;
    const novasPermissoes = [...permissoes];
    const index = novasPermissoes.findIndex(p => p.colaboradorId === colaboradorSelecionado);
    const cfg = permissoesColaborador.permissoesGerais.configurarEtapas;
    const etapasAtuais = cfg.etapas || [];
    const novasEtapas = etapasAtuais.includes(etapaId)
      ? etapasAtuais.filter(id => id !== etapaId)
      : [...etapasAtuais, etapaId];
    const permissaoAtualizada = {
      ...permissoesColaborador,
      permissoesGerais: {
        ...permissoesColaborador.permissoesGerais,
        configurarEtapas: { ...cfg, etapas: novasEtapas },
      },
    };
    if (index >= 0) novasPermissoes[index] = permissaoAtualizada;
    else novasPermissoes.push(permissaoAtualizada);
    setPermissoes(novasPermissoes);
  };

  // Adicionar/remover seção da permissão de configurar etapas
  const toggleSecaoConfigurar = (secao: string) => {
    if (!colaboradorSelecionado || !permissoesColaborador) return;
    const novasPermissoes = [...permissoes];
    const index = novasPermissoes.findIndex(p => p.colaboradorId === colaboradorSelecionado);
    const cfg = permissoesColaborador.permissoesGerais.configurarEtapas;
    const secoesAtuais = cfg.secoes || [];
    const novasSecoes = secoesAtuais.includes(secao)
      ? secoesAtuais.filter(s => s !== secao)
      : [...secoesAtuais, secao];
    const permissaoAtualizada = {
      ...permissoesColaborador,
      permissoesGerais: {
        ...permissoesColaborador.permissoesGerais,
        configurarEtapas: { ...cfg, secoes: novasSecoes },
      },
    };
    if (index >= 0) novasPermissoes[index] = permissaoAtualizada;
    else novasPermissoes.push(permissaoAtualizada);
    setPermissoes(novasPermissoes);
  };

  // Adicionar/remover etapa de uma permissão
  const toggleEtapaPermissao = (permissao: 'criarCards' | 'editarCards' | 'deletarCards' | 'transformarEmPai', etapaId: string) => {
    if (!colaboradorSelecionado || !permissoesColaborador) return;
    
    console.log('🎯 Toggle etapa:', permissao, etapaId);
    
    const novasPermissoes = [...permissoes];
    const index = novasPermissoes.findIndex(p => p.colaboradorId === colaboradorSelecionado);
    
    const permissaoAtual = permissoesColaborador.permissoesGerais[permissao] as { permitido: boolean; etapas?: string[] };
    const etapasAtuais = permissaoAtual.etapas || [];
    const novasEtapas = etapasAtuais.includes(etapaId)
      ? etapasAtuais.filter(id => id !== etapaId)
      : [...etapasAtuais, etapaId];
    
    const permissaoAtualizada = {
      ...permissoesColaborador,
      permissoesGerais: {
        ...permissoesColaborador.permissoesGerais,
        [permissao]: {
          ...permissaoAtual,
          etapas: novasEtapas,
        },
      },
    };
    
    if (index >= 0) {
      novasPermissoes[index] = permissaoAtualizada;
    } else {
      novasPermissoes.push(permissaoAtualizada);
    }
    
    setPermissoes(novasPermissoes);
  };

  // Adicionar/remover campo da permissão de editar
  const toggleCampoPermissaoEditar = (campoId: string) => {
    if (!colaboradorSelecionado || !permissoesColaborador) return;
    
    console.log('📝 Toggle campo:', campoId);
    
    const novasPermissoes = [...permissoes];
    const index = novasPermissoes.findIndex(p => p.colaboradorId === colaboradorSelecionado);
    
    const permissaoAtual = permissoesColaborador.permissoesGerais.editarCards;
    const camposAtuais = permissaoAtual.campos || [];
    const novosCampos = camposAtuais.includes(campoId)
      ? camposAtuais.filter(id => id !== campoId)
      : [...camposAtuais, campoId];
    
    const permissaoAtualizada = {
      ...permissoesColaborador,
      permissoesGerais: {
        ...permissoesColaborador.permissoesGerais,
        editarCards: {
          ...permissaoAtual,
          campos: novosCampos,
        },
      },
    };
    
    if (index >= 0) {
      novasPermissoes[index] = permissaoAtualizada;
    } else {
      novasPermissoes.push(permissaoAtualizada);
    }
    
    setPermissoes(novasPermissoes);
  };

  // Atualizar permissões por etapa
  const togglePermissaoEtapa = (etapaId: string, chave: 'acessar' | 'moverPara' | 'moverDe') => {
    if (!colaboradorSelecionado || !permissoesColaborador) return;
    
    const novasPermissoes = [...permissoes];
    const index = novasPermissoes.findIndex(p => p.colaboradorId === colaboradorSelecionado);
    
    const permissoesEtapaAtual = permissoesColaborador.permissoesPorEtapa[etapaId] || {
      acessar: true,
      moverPara: true,
      moverDe: true,
    };
    
    const permissaoAtualizada = {
      ...permissoesColaborador,
      permissoesPorEtapa: {
        ...permissoesColaborador.permissoesPorEtapa,
        [etapaId]: {
          ...permissoesEtapaAtual,
          [chave]: !permissoesEtapaAtual[chave],
        },
      },
    };
    
    if (index >= 0) {
      novasPermissoes[index] = permissaoAtualizada;
    } else {
      novasPermissoes.push(permissaoAtualizada);
    }
    
    setPermissoes(novasPermissoes);
  };

  // Atualizar escopo de cards visíveis por etapa
  const setEscopoEtapa = (etapaId: string, escopo: 'todos' | 'proprios' | 'responsavel' | 'mencionado') => {
    if (!colaboradorSelecionado || !permissoesColaborador) return;

    const novasPermissoes = [...permissoes];
    const index = novasPermissoes.findIndex(p => p.colaboradorId === colaboradorSelecionado);

    const permissoesEtapaAtual = permissoesColaborador.permissoesPorEtapa[etapaId] || {
      acessar: true,
      moverPara: true,
      moverDe: true,
    };

    const permissaoAtualizada = {
      ...permissoesColaborador,
      permissoesPorEtapa: {
        ...permissoesColaborador.permissoesPorEtapa,
        [etapaId]: {
          ...permissoesEtapaAtual,
          escopoCards: escopo,
        },
      },
    };

    if (index >= 0) {
      novasPermissoes[index] = permissaoAtualizada;
    } else {
      novasPermissoes.push(permissaoAtualizada);
    }

    setPermissoes(novasPermissoes);
  };

  // Atualizar permissões por campo
  const togglePermissaoCampo = (campoId: string, chave: 'visualizar' | 'editar') => {
    if (!colaboradorSelecionado || !permissoesColaborador) return;
    
    const novasPermissoes = [...permissoes];
    const index = novasPermissoes.findIndex(p => p.colaboradorId === colaboradorSelecionado);
    
    const permissoesCampoAtual = permissoesColaborador.permissoesPorCampo[campoId] || {
      visualizar: true,
      editar: true,
    };
    
    const permissaoAtualizada = {
      ...permissoesColaborador,
      permissoesPorCampo: {
        ...permissoesColaborador.permissoesPorCampo,
        [campoId]: {
          ...permissoesCampoAtual,
          [chave]: !permissoesCampoAtual[chave],
        },
      },
    };
    
    if (index >= 0) {
      novasPermissoes[index] = permissaoAtualizada;
    } else {
      novasPermissoes.push(permissaoAtualizada);
    }
    
    setPermissoes(novasPermissoes);
  };

  // Toggle para permissão de portal (visualizar/editar)
  const togglePermissaoPortal = (chave: 'visualizar' | 'editar') => {
    if (!colaboradorSelecionado || !permissoesColaborador) return;
    
    const novasPermissoes = [...permissoes];
    const index = novasPermissoes.findIndex(p => p.colaboradorId === colaboradorSelecionado);
    
    const portalAtual = permissoesColaborador.permissoesGerais.gerenciarPortal || { visualizar: false, editar: false };
    const novoPortal = { ...portalAtual, [chave]: !portalAtual[chave] };
    // Se editar está ativo, visualizar também deve estar
    if (chave === 'editar' && novoPortal.editar) novoPortal.visualizar = true;
    // Se desativar visualizar, editar também sai
    if (chave === 'visualizar' && !novoPortal.visualizar) novoPortal.editar = false;
    
    const permissaoAtualizada = {
      ...permissoesColaborador,
      permissoesGerais: {
        ...permissoesColaborador.permissoesGerais,
        gerenciarPortal: novoPortal,
      },
    };
    
    if (index >= 0) {
      novasPermissoes[index] = permissaoAtualizada;
    } else {
      novasPermissoes.push(permissaoAtualizada);
    }
    
    setPermissoes(novasPermissoes);
  };

  // Todos os campos únicos do fluxo
  const todosCampos = useMemo(() => {
    const camposMap = new Map();
    flow.etapas?.forEach(etapa => {
      etapa.campos?.forEach(campo => {
        if (!camposMap.has(campo.id)) {
          camposMap.set(campo.id, {
            ...campo,
            etapaNome: etapa.nome,
          });
        }
      });
    });
    return Array.from(camposMap.values());
  }, [flow.etapas]);

  const handleSalvar = () => {
    console.log('🔒 Salvando permissões:', JSON.stringify(permissoes, null, 2));
    onSave(permissoes, {
      habilitado: cotacaoHabilitada,
      rotulo: cotacaoRotulo.trim() || undefined,
    }, {
      habilitado: prazoHabilitado && Number(prazoHoras) > 0,
      horas: Number(prazoHoras) || 0,
      ignorarFimDeSemana: prazoIgnorarFds,
      etapasPausaIds: prazoPausa,
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <div>
            <h2 className="text-base font-bold text-gray-900">Configuração de Permissões</h2>
            <p className="text-xs text-gray-500 mt-0.5">{flow.nome}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* 💰 Cotação com fornecedor — do fluxo inteiro, não de uma etapa nem de um colaborador.
            Fica fora da lista de colaboradores de propósito: não é permissão individual. */}
        <div className="px-6 py-3 border-b border-gray-100 bg-emerald-50/40 shrink-0">
          <div className="flex items-center gap-1.5 mb-1.5">
            <span className="text-[9px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded">
              Configuração do fluxo
            </span>
            <span className="text-[10px] text-gray-500">
              vale para todos os colaboradores — não depende de quem está selecionado ao lado
            </span>
          </div>
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-gray-800">💰 Cotação com fornecedor</h3>
              <p className="text-[11px] text-gray-500 mt-0.5">
                Libera o botão de pedir cotação nos cards deste fluxo. Cada fornecedor recebe um
                link próprio e a resposta volta como card filho.
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer flex-none mt-0.5">
              <input
                type="checkbox"
                checked={cotacaoHabilitada}
                onChange={(e) => setCotacaoHabilitada(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:ring-2 peer-focus:ring-emerald-300 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500"></div>
            </label>
          </div>

          {cotacaoHabilitada && (
            <div className="mt-3">
              <label className="block text-[11px] font-medium text-gray-700 mb-1">
                Texto do botão no card <span className="text-gray-400 font-normal">(opcional)</span>
              </label>
              <input
                type="text"
                value={cotacaoRotulo}
                onChange={(e) => setCotacaoRotulo(e.target.value)}
                placeholder="Pedir cotação"
                className="w-full max-w-sm px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />

              <p className="text-[11px] text-gray-500 mt-2">
                Os campos que o fornecedor vê e preenche são configurados por etapa, em{' '}
                <strong>Configurar Etapa · Campos · Cotação com fornecedor</strong>.
              </p>
            </div>
          )}
        </div>

        {/* ⏱️ Prazo do processo — também do fluxo inteiro */}
        <div className="px-6 py-3 border-b border-gray-100 bg-sky-50/40 shrink-0">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-gray-800">⏱️ Prazo do processo</h3>
              <p className="text-[11px] text-gray-500 mt-0.5">
                Um prazo só para o card inteiro, contando desde a criação. Mudar de etapa não dá tempo novo.
                Nas etapas de pausa o prazo congela; quando o card sai delas (ex.: reenvio → reanálise) começa um prazo novo, cheio.
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer flex-none mt-0.5">
              <input
                type="checkbox"
                checked={prazoHabilitado}
                onChange={(e) => setPrazoHabilitado(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:ring-2 peer-focus:ring-sky-300 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-sky-500"></div>
            </label>
          </div>

          {prazoHabilitado && (
            <div className="mt-3 space-y-3">
              <div className="flex flex-wrap items-center gap-4">
                <label className="flex items-center gap-2 text-[11px] font-medium text-gray-700">
                  Prazo
                  <input
                    type="number"
                    min={1}
                    value={prazoHoras}
                    onChange={(e) => setPrazoHoras(e.target.value)}
                    className="w-20 px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                  />
                  horas
                </label>
                <label className="flex items-center gap-2 text-[11px] text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={prazoIgnorarFds}
                    onChange={(e) => setPrazoIgnorarFds(e.target.checked)}
                    className="rounded border-gray-300 text-sky-600 focus:ring-sky-500"
                  />
                  Não contar sábado e domingo
                </label>
              </div>
              <div>
                <div className="text-[11px] font-medium text-gray-700 mb-1">
                  Etapas que pausam o prazo <span className="text-gray-400 font-normal">(ex.: Reprovados, Concluídos)</span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {(flow.etapas || []).map(et => {
                    const id = String(et.id);
                    const marcada = prazoPausa.includes(id);
                    return (
                      <button
                        key={id}
                        type="button"
                        onClick={() => setPrazoPausa(prev => marcada ? prev.filter(x => x !== id) : [...prev, id])}
                        className={`px-2 py-1 rounded-full text-[11px] border transition ${
                          marcada ? 'bg-sky-600 border-sky-600 text-white' : 'bg-white border-gray-300 text-gray-600 hover:border-sky-400'
                        }`}
                      >
                        {marcada ? '⏸ ' : ''}{et.nome}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Conteúdo principal */}
        <div className="flex flex-1 min-h-0">
          {/* Lista de colaboradores */}
          <div className="w-64 border-r border-gray-100 flex flex-col shrink-0">
            <div className="p-3 border-b border-gray-100">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <input
                  value={pesquisa}
                  onChange={(e) => setPesquisa(e.target.value)}
                  placeholder="Buscar colaborador..."
                  className="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              {colaboradoresFiltrados.map((colaborador, idx) => {
                const isSelected = colaboradorSelecionado === colaborador.id;
                return (
                  <button
                    key={colaborador.id}
                    onClick={() => {
                      setColaboradorSelecionado(colaborador.id);
                    }}
                    className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left transition-colors hover:bg-gray-50 ${isSelected ? 'bg-indigo-50 border-r-2 border-indigo-500' : ''}`}
                  >
                    <div className={`w-7 h-7 rounded-full ${AVATAR_COLORS[idx % AVATAR_COLORS.length]} flex items-center justify-center text-white text-[10px] font-bold shrink-0 overflow-hidden`}>
                      {colaborador.foto ? <img src={colaborador.foto} alt="" className="w-full h-full object-cover" /> : getInitials(colaborador.nome || '?')}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium text-gray-800 truncate">{colaborador.nome}</div>
                      <div className="text-[10px] text-gray-400 truncate">{colaborador.email}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Painel de permissões */}
          <div className="flex-1 overflow-y-auto p-5">
            {!colaboradorSelecionado ? (
              <div className="h-full flex flex-col items-center justify-center text-center text-gray-400">
                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                </div>
                <p className="text-sm font-medium text-gray-500">Selecione um colaborador</p>
                <p className="text-xs mt-1">para configurar suas permissões de fluxo</p>
              </div>
            ) : permissoesColaborador && (
              <div className="space-y-2">
                
                {/* ═══════════════ VISUALIZAR ETAPAS ═══════════════ */}
                {(() => {
                  const etapasOcultas = flow.etapas?.filter(e => {
                    const pe = permissoesColaborador.permissoesPorEtapa[e.id];
                    return pe && pe.acessar === false;
                  }).length || 0;
                  const totalEtapas = flow.etapas?.length || 0;
                  const algumaOculta = etapasOcultas > 0;
                  return (
                    <div className={`border rounded-lg overflow-hidden ${algumaOculta ? 'border-blue-200 bg-blue-50/30' : 'border-gray-200'}`}>
                      <div className="flex items-center justify-between px-4 py-3">
                        <div className="flex items-center gap-3">
                          <span className="text-lg">👁️</span>
                          <div>
                            <p className="font-semibold text-sm text-gray-900">Visualizar etapas</p>
                            <p className="text-[11px] text-gray-500">
                              {algumaOculta
                                ? `Vendo ${totalEtapas - etapasOcultas} de ${totalEtapas} etapa(s) — ${etapasOcultas} oculta(s)`
                                : `Vendo todas as ${totalEtapas} etapa(s)`}
                            </p>
                          </div>
                        </div>
                        <button onClick={() => setPermissaoExpandida(permissaoExpandida === 'visualizar' ? null : 'visualizar')} className="text-gray-400 hover:text-gray-600 text-xs px-1">
                          {permissaoExpandida === 'visualizar' ? '▲' : '▼'}
                        </button>
                      </div>
                      {permissaoExpandida === 'visualizar' && (
                        <div className="border-t border-gray-200 px-4 py-3 bg-white/80">
                          <p className="text-xs font-semibold text-gray-600 mb-2">📍 Quais etapas este colaborador pode ver?</p>
                          <p className="text-[11px] text-gray-500 mb-2">Desmarque para ocultar a etapa (e seus cards) do colaborador no Kanban, Lista, Fluxo e Relatórios. O escopo controla quais cards da etapa ele enxerga.</p>
                          <div className="space-y-1">
                            {flow.etapas?.map(etapa => {
                              const pe = permissoesColaborador.permissoesPorEtapa[etapa.id] || { acessar: true, moverPara: true, moverDe: true, escopoCards: 'todos' as const };
                              const podeVer = pe.acessar !== false;
                              return (
                                <div key={etapa.id} className="flex items-center gap-2 py-1 px-2 hover:bg-gray-50 rounded text-xs">
                                  <label className="flex items-center gap-1.5 cursor-pointer flex-1 min-w-0">
                                    <input
                                      type="checkbox"
                                      checked={podeVer}
                                      onChange={() => togglePermissaoEtapa(etapa.id, 'acessar')}
                                      className="w-3.5 h-3.5 text-blue-600 rounded shrink-0"
                                    />
                                    <span className="truncate">{etapa.nome}</span>
                                  </label>
                                  <select
                                    disabled={!podeVer}
                                    value={pe.escopoCards || 'todos'}
                                    onChange={(e) => setEscopoEtapa(etapa.id, e.target.value as 'todos' | 'proprios' | 'responsavel' | 'mencionado')}
                                    className="text-[11px] border border-gray-300 rounded px-1.5 py-0.5 bg-white disabled:bg-gray-100 disabled:text-gray-400 shrink-0"
                                    title="Quais cards desta etapa ele enxerga"
                                  >
                                    <option value="todos">Todos os cards</option>
                                    <option value="proprios">Apenas criados por ele</option>
                                    <option value="responsavel">Apenas onde é responsável</option>
                                    <option value="mencionado">Apenas onde o nome dele aparece</option>
                                  </select>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* ═══════════════ CRIAR CARDS ═══════════════ */}
                <div className={`border rounded-lg overflow-hidden ${permissoesColaborador.permissoesGerais.criarCards.permitido ? 'border-green-200 bg-green-50/30' : 'border-gray-200'}`}>
                  <div className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-center gap-3">
                      <span className="text-lg">📄</span>
                      <div>
                        <p className="font-semibold text-sm text-gray-900">Criar novos cards</p>
                        <p className="text-[11px] text-gray-500">
                          {permissoesColaborador.permissoesGerais.criarCards.permitido
                            ? `Permitido em ${permissoesColaborador.permissoesGerais.criarCards.etapas?.length || 0} etapa(s)`
                            : 'Bloqueado'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div
                        onClick={() => togglePermissaoGeral('criarCards')}
                        className={`w-11 h-6 rounded-full transition-colors cursor-pointer relative ${
                          permissoesColaborador.permissoesGerais.criarCards.permitido ? 'bg-green-500' : 'bg-gray-300'
                        }`}
                      >
                        <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform absolute top-0.5 ${
                          permissoesColaborador.permissoesGerais.criarCards.permitido ? 'left-[22px]' : 'left-0.5'
                        }`}></div>
                      </div>
                      {permissoesColaborador.permissoesGerais.criarCards.permitido && (
                        <button onClick={() => setPermissaoExpandida(permissaoExpandida === 'criar' ? null : 'criar')} className="text-gray-400 hover:text-gray-600 text-xs px-1">
                          {permissaoExpandida === 'criar' ? '▲' : '▼'}
                        </button>
                      )}
                    </div>
                  </div>
                  {permissaoExpandida === 'criar' && permissoesColaborador.permissoesGerais.criarCards.permitido && (
                    <div className="border-t border-gray-200 px-4 py-3 bg-white/80">
                      <p className="text-xs font-semibold text-gray-600 mb-2">📍 Em quais etapas pode criar?</p>
                      <div className="grid grid-cols-3 gap-1">
                        {flow.etapas?.map(etapa => (
                          <label key={etapa.id} className="flex items-center gap-1.5 cursor-pointer py-1 px-2 hover:bg-gray-50 rounded text-xs">
                            <input type="checkbox" checked={permissoesColaborador.permissoesGerais.criarCards.etapas?.includes(etapa.id)} onChange={() => toggleEtapaPermissao('criarCards', etapa.id)} className="w-3.5 h-3.5 text-blue-600 rounded" />
                            <span className="truncate">{etapa.nome}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* ═══════════════ EDITAR CARDS ═══════════════ */}
                <div className={`border rounded-lg overflow-hidden ${permissoesColaborador.permissoesGerais.editarCards.permitido ? 'border-green-200 bg-green-50/30' : 'border-gray-200'}`}>
                  <div className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-center gap-3">
                      <span className="text-lg">✏️</span>
                      <div>
                        <p className="font-semibold text-sm text-gray-900">Editar cards</p>
                        <p className="text-[11px] text-gray-500">
                          {permissoesColaborador.permissoesGerais.editarCards.permitido
                            ? `${permissoesColaborador.permissoesGerais.editarCards.etapas?.length || 0} etapa(s) · ${permissoesColaborador.permissoesGerais.editarCards.campos?.length || 0} campo(s)`
                            : 'Bloqueado'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div
                        onClick={() => togglePermissaoGeral('editarCards')}
                        className={`w-11 h-6 rounded-full transition-colors cursor-pointer relative ${
                          permissoesColaborador.permissoesGerais.editarCards.permitido ? 'bg-green-500' : 'bg-gray-300'
                        }`}
                      >
                        <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform absolute top-0.5 ${
                          permissoesColaborador.permissoesGerais.editarCards.permitido ? 'left-[22px]' : 'left-0.5'
                        }`}></div>
                      </div>
                      {permissoesColaborador.permissoesGerais.editarCards.permitido && (
                        <button onClick={() => setPermissaoExpandida(permissaoExpandida === 'editar' ? null : 'editar')} className="text-gray-400 hover:text-gray-600 text-xs px-1">
                          {permissaoExpandida === 'editar' ? '▲' : '▼'}
                        </button>
                      )}
                    </div>
                  </div>
                  {permissaoExpandida === 'editar' && permissoesColaborador.permissoesGerais.editarCards.permitido && (
                    <div className="border-t border-gray-200 px-4 py-3 bg-white/80 space-y-3">
                      <div>
                        <p className="text-xs font-semibold text-gray-600 mb-2">📍 Em quais etapas pode editar?</p>
                        <div className="grid grid-cols-3 gap-1">
                          {flow.etapas?.map(etapa => (
                            <label key={etapa.id} className="flex items-center gap-1.5 cursor-pointer py-1 px-2 hover:bg-gray-50 rounded text-xs">
                              <input type="checkbox" checked={permissoesColaborador.permissoesGerais.editarCards.etapas?.includes(etapa.id)} onChange={() => toggleEtapaPermissao('editarCards', etapa.id)} className="w-3.5 h-3.5 text-blue-600 rounded" />
                              <span className="truncate">{etapa.nome}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-gray-600 mb-2">📝 Quais campos pode editar?</p>
                        <div className="grid grid-cols-3 gap-1 max-h-36 overflow-y-auto">
                          {todosCampos.map(campo => (
                            <label key={campo.id} className="flex items-center gap-1.5 cursor-pointer py-1 px-2 hover:bg-gray-50 rounded text-xs">
                              <input type="checkbox" checked={permissoesColaborador.permissoesGerais.editarCards.campos?.includes(campo.id)} onChange={() => toggleCampoPermissaoEditar(campo.id)} className="w-3.5 h-3.5 text-blue-600 rounded" />
                              <span className="truncate" title={`${campo.nome} (${campo.etapaNome})`}>{campo.nome}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* ═══════════════ DELETAR CARDS ═══════════════ */}
                <div className={`border rounded-lg overflow-hidden ${permissoesColaborador.permissoesGerais.deletarCards.permitido ? 'border-red-200 bg-red-50/30' : 'border-gray-200'}`}>
                  <div className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-center gap-3">
                      <span className="text-lg">🗑️</span>
                      <div>
                        <p className="font-semibold text-sm text-gray-900">Deletar cards</p>
                        <p className="text-[11px] text-gray-500">
                          {permissoesColaborador.permissoesGerais.deletarCards.permitido
                            ? `Permitido em ${permissoesColaborador.permissoesGerais.deletarCards.etapas?.length || 0} etapa(s)`
                            : 'Bloqueado'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div
                        onClick={() => togglePermissaoGeral('deletarCards')}
                        className={`w-11 h-6 rounded-full transition-colors cursor-pointer relative ${
                          permissoesColaborador.permissoesGerais.deletarCards.permitido ? 'bg-green-500' : 'bg-gray-300'
                        }`}
                      >
                        <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform absolute top-0.5 ${
                          permissoesColaborador.permissoesGerais.deletarCards.permitido ? 'left-[22px]' : 'left-0.5'
                        }`}></div>
                      </div>
                      {permissoesColaborador.permissoesGerais.deletarCards.permitido && (
                        <button onClick={() => setPermissaoExpandida(permissaoExpandida === 'deletar' ? null : 'deletar')} className="text-gray-400 hover:text-gray-600 text-xs px-1">
                          {permissaoExpandida === 'deletar' ? '▲' : '▼'}
                        </button>
                      )}
                    </div>
                  </div>
                  {permissaoExpandida === 'deletar' && permissoesColaborador.permissoesGerais.deletarCards.permitido && (
                    <div className="border-t border-gray-200 px-4 py-3 bg-white/80">
                      <p className="text-xs font-semibold text-gray-600 mb-2">📍 Em quais etapas pode deletar?</p>
                      <div className="grid grid-cols-3 gap-1">
                        {flow.etapas?.map(etapa => (
                          <label key={etapa.id} className="flex items-center gap-1.5 cursor-pointer py-1 px-2 hover:bg-gray-50 rounded text-xs">
                            <input type="checkbox" checked={permissoesColaborador.permissoesGerais.deletarCards.etapas?.includes(etapa.id)} onChange={() => toggleEtapaPermissao('deletarCards', etapa.id)} className="w-3.5 h-3.5 text-blue-600 rounded" />
                            <span className="truncate">{etapa.nome}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* ═══════════════ MOVER CARDS ═══════════════ */}
                <div className={`border rounded-lg overflow-hidden ${permissoesColaborador.permissoesGerais.moverCards.permitido ? 'border-blue-200 bg-blue-50/30' : 'border-gray-200'}`}>
                  <div className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-center gap-3">
                      <span className="text-lg">↔️</span>
                      <div>
                        <p className="font-semibold text-sm text-gray-900">Mover cards entre etapas</p>
                        <p className="text-[11px] text-gray-500">
                          {permissoesColaborador.permissoesGerais.moverCards.permitido ? 'Permitido' : 'Bloqueado'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div
                        onClick={() => togglePermissaoGeral('moverCards')}
                        className={`w-11 h-6 rounded-full transition-colors cursor-pointer relative ${
                          permissoesColaborador.permissoesGerais.moverCards.permitido ? 'bg-green-500' : 'bg-gray-300'
                        }`}
                      >
                        <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform absolute top-0.5 ${
                          permissoesColaborador.permissoesGerais.moverCards.permitido ? 'left-[22px]' : 'left-0.5'
                        }`}></div>
                      </div>
                      {permissoesColaborador.permissoesGerais.moverCards.permitido && (
                        <button onClick={() => setPermissaoExpandida(permissaoExpandida === 'mover' ? null : 'mover')} className="text-gray-400 hover:text-gray-600 text-xs px-1">
                          {permissaoExpandida === 'mover' ? '▲' : '▼'}
                        </button>
                      )}
                    </div>
                  </div>
                  {permissaoExpandida === 'mover' && permissoesColaborador.permissoesGerais.moverCards.permitido && (
                    <div className="border-t border-gray-200 px-4 py-3 bg-white/80">
                      <p className="text-xs font-semibold text-gray-600 mb-2">📍 Acesso por etapa (visualizar / mover para / mover de)</p>
                      <div className="space-y-1.5">
                        {flow.etapas?.map(etapa => {
                          const pe = permissoesColaborador.permissoesPorEtapa[etapa.id] || { acessar: true, moverPara: true, moverDe: true };
                          return (
                            <div key={etapa.id} className="flex items-center gap-2 py-1 px-2 bg-gray-50 rounded text-xs">
                              <span className="font-medium text-gray-800 w-40 truncate">{etapa.nome}</span>
                              <label className="flex items-center gap-1 cursor-pointer">
                                <input type="checkbox" checked={pe.acessar} onChange={() => togglePermissaoEtapa(etapa.id, 'acessar')} className="w-3.5 h-3.5 text-blue-600 rounded" />
                                <span className="text-gray-600">Ver</span>
                              </label>
                              <label className="flex items-center gap-1 cursor-pointer">
                                <input type="checkbox" checked={pe.moverPara} onChange={() => togglePermissaoEtapa(etapa.id, 'moverPara')} className="w-3.5 h-3.5 text-blue-600 rounded" />
                                <span className="text-gray-600">→ Para</span>
                              </label>
                              <label className="flex items-center gap-1 cursor-pointer">
                                <input type="checkbox" checked={pe.moverDe} onChange={() => togglePermissaoEtapa(etapa.id, 'moverDe')} className="w-3.5 h-3.5 text-blue-600 rounded" />
                                <span className="text-gray-600">← De</span>
                              </label>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                {/* ═══════════════ TRANSFORMAR EM PAI ═══════════════ */}
                <div className={`border rounded-lg overflow-hidden ${permissoesColaborador.permissoesGerais.transformarEmPai.permitido ? 'border-purple-200 bg-purple-50/30' : 'border-gray-200'}`}>
                  <div className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-center gap-3">
                      <span className="text-lg">👨‍👧‍👦</span>
                      <div>
                        <p className="font-semibold text-sm text-gray-900">Transformar em card pai</p>
                        <p className="text-[11px] text-gray-500">
                          {permissoesColaborador.permissoesGerais.transformarEmPai.permitido
                            ? `Permitido em ${permissoesColaborador.permissoesGerais.transformarEmPai.etapas?.length || 0} etapa(s)`
                            : 'Bloqueado'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div
                        onClick={() => togglePermissaoGeral('transformarEmPai')}
                        className={`w-11 h-6 rounded-full transition-colors cursor-pointer relative ${
                          permissoesColaborador.permissoesGerais.transformarEmPai.permitido ? 'bg-green-500' : 'bg-gray-300'
                        }`}
                      >
                        <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform absolute top-0.5 ${
                          permissoesColaborador.permissoesGerais.transformarEmPai.permitido ? 'left-[22px]' : 'left-0.5'
                        }`}></div>
                      </div>
                      {permissoesColaborador.permissoesGerais.transformarEmPai.permitido && (
                        <button onClick={() => setPermissaoExpandida(permissaoExpandida === 'transformar' ? null : 'transformar')} className="text-gray-400 hover:text-gray-600 text-xs px-1">
                          {permissaoExpandida === 'transformar' ? '▲' : '▼'}
                        </button>
                      )}
                    </div>
                  </div>
                  {permissaoExpandida === 'transformar' && permissoesColaborador.permissoesGerais.transformarEmPai.permitido && (
                    <div className="border-t border-gray-200 px-4 py-3 bg-white/80">
                      <p className="text-xs font-semibold text-gray-600 mb-2">📍 Em quais etapas pode transformar?</p>
                      <div className="grid grid-cols-3 gap-1">
                        {flow.etapas?.map(etapa => (
                          <label key={etapa.id} className="flex items-center gap-1.5 cursor-pointer py-1 px-2 hover:bg-gray-50 rounded text-xs">
                            <input type="checkbox" checked={permissoesColaborador.permissoesGerais.transformarEmPai.etapas?.includes(etapa.id)} onChange={() => toggleEtapaPermissao('transformarEmPai', etapa.id)} className="w-3.5 h-3.5 text-blue-600 rounded" />
                            <span className="truncate">{etapa.nome}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* ═══════════════ CONFIGURAR ETAPAS ═══════════════ */}
                {(() => {
                  const cfg = permissoesColaborador.permissoesGerais.configurarEtapas;
                  // backward compat: pode vir como boolean de dados antigos
                  const cfgObj = typeof cfg === 'boolean'
                    ? { permitido: cfg as unknown as boolean, etapas: [], secoes: [] }
                    : cfg;
                  const ativo = cfgObj.permitido;
                  const secoesDisponiveis = [
                    { id: 'geral', label: 'Geral', desc: 'Nome, departamento e regras' },
                    { id: 'participantes', label: 'Equipe', desc: 'Participantes da etapa' },
                    { id: 'campos', label: 'Campos', desc: 'Campos e formulários' },
                    { id: 'fluxo', label: 'Fluxo', desc: 'Criação e movimentação' },
                    { id: 'notificacoes', label: 'Alertas', desc: 'E-mail e WhatsApp' },
                    { id: 'templates', label: 'Cards Personalizados', desc: 'Modelos pré-preenchidos' },
                  ];
                  const todasEtapas = !cfgObj.etapas || cfgObj.etapas.length === 0;
                  const todasSecoes = !cfgObj.secoes || cfgObj.secoes.length === 0;
                  return (
                    <div className={`border rounded-lg overflow-hidden ${ativo ? 'border-amber-200 bg-amber-50/30' : 'border-gray-200'}`}>
                      <div className="flex items-center justify-between px-4 py-3">
                        <div className="flex items-center gap-3">
                          <span className="text-lg">⚙️</span>
                          <div>
                            <p className="font-semibold text-sm text-gray-900">Configurar etapas</p>
                            <p className="text-[11px] text-gray-500">
                              {ativo
                                ? `${todasEtapas ? 'Todas etapas' : `${cfgObj.etapas!.length} etapa(s)`} · ${todasSecoes ? 'todas as seções' : `${cfgObj.secoes!.length} seção(ões)`}`
                                : 'Bloqueado'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <div
                            onClick={() => togglePermissaoGeral('configurarEtapas')}
                            className={`w-11 h-6 rounded-full transition-colors cursor-pointer relative ${
                              ativo ? 'bg-green-500' : 'bg-gray-300'
                            }`}
                          >
                            <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform absolute top-0.5 ${
                              ativo ? 'left-[22px]' : 'left-0.5'
                            }`}></div>
                          </div>
                          {ativo && (
                            <button onClick={() => setPermissaoExpandida(permissaoExpandida === 'configurarEtapas' ? null : 'configurarEtapas')} className="text-gray-400 hover:text-gray-600 text-xs px-1">
                              {permissaoExpandida === 'configurarEtapas' ? '▲' : '▼'}
                            </button>
                          )}
                        </div>
                      </div>
                      {permissaoExpandida === 'configurarEtapas' && ativo && (
                        <div className="border-t border-gray-200 px-4 py-3 bg-white/80 space-y-4">
                          {/* Quais etapas */}
                          <div>
                            <p className="text-xs font-semibold text-gray-600 mb-2">📍 Em quais etapas pode configurar?</p>
                            <label className="flex items-center gap-1.5 cursor-pointer py-1 px-2 hover:bg-gray-50 rounded text-xs mb-1">
                              <input type="checkbox" checked={todasEtapas}
                                onChange={() => {
                                  const novas = [...permissoes];
                                  const idx = novas.findIndex(p => p.colaboradorId === colaboradorSelecionado);
                                  const upd = { ...permissoesColaborador, permissoesGerais: { ...permissoesColaborador.permissoesGerais, configurarEtapas: { ...cfgObj, etapas: [] } } };
                                  if (idx >= 0) novas[idx] = upd; else novas.push(upd);
                                  setPermissoes(novas);
                                }}
                                className="w-3.5 h-3.5 text-amber-600 rounded" />
                              <span className="font-medium text-amber-700">Todas as etapas</span>
                            </label>
                            {!todasEtapas && (
                              <div className="grid grid-cols-3 gap-1 ml-2">
                                {flow.etapas?.map(etapa => (
                                  <label key={etapa.id} className="flex items-center gap-1.5 cursor-pointer py-1 px-2 hover:bg-gray-50 rounded text-xs">
                                    <input type="checkbox" checked={cfgObj.etapas?.includes(etapa.id)}
                                      onChange={() => toggleEtapaConfigurar(etapa.id)}
                                      className="w-3.5 h-3.5 text-amber-600 rounded" />
                                    <span className="truncate">{etapa.nome}</span>
                                  </label>
                                ))}
                              </div>
                            )}
                            {todasEtapas && (
                              <div className="grid grid-cols-3 gap-1 ml-2">
                                {flow.etapas?.map(etapa => (
                                  <label key={etapa.id} className="flex items-center gap-1.5 cursor-pointer py-1 px-2 hover:bg-gray-50 rounded text-xs">
                                    <input type="checkbox" checked={true}
                                      onChange={() => toggleEtapaConfigurar(etapa.id)}
                                      className="w-3.5 h-3.5 text-amber-600 rounded" />
                                    <span className="truncate">{etapa.nome}</span>
                                  </label>
                                ))}
                              </div>
                            )}
                          </div>
                          {/* Quais seções */}
                          <div>
                            <p className="text-xs font-semibold text-gray-600 mb-2">🔧 Quais seções pode configurar?</p>
                            <label className="flex items-center gap-1.5 cursor-pointer py-1 px-2 hover:bg-gray-50 rounded text-xs mb-1">
                              <input type="checkbox" checked={todasSecoes}
                                onChange={() => {
                                  const novas = [...permissoes];
                                  const idx = novas.findIndex(p => p.colaboradorId === colaboradorSelecionado);
                                  const upd = { ...permissoesColaborador, permissoesGerais: { ...permissoesColaborador.permissoesGerais, configurarEtapas: { ...cfgObj, secoes: [] } } };
                                  if (idx >= 0) novas[idx] = upd; else novas.push(upd);
                                  setPermissoes(novas);
                                }}
                                className="w-3.5 h-3.5 text-amber-600 rounded" />
                              <span className="font-medium text-amber-700">Todas as seções</span>
                            </label>
                            <div className="grid grid-cols-2 gap-1 ml-2">
                              {secoesDisponiveis.map(sec => (
                                <label key={sec.id} className="flex items-center gap-2 cursor-pointer py-1.5 px-2 hover:bg-gray-50 rounded text-xs">
                                  <input type="checkbox"
                                    checked={todasSecoes || (cfgObj.secoes?.includes(sec.id) ?? false)}
                                    onChange={() => { if (todasSecoes) { /* primeiro desmarca todas */ const novas = [...permissoes]; const idx = novas.findIndex(p => p.colaboradorId === colaboradorSelecionado); const upd = { ...permissoesColaborador, permissoesGerais: { ...permissoesColaborador.permissoesGerais, configurarEtapas: { ...cfgObj, secoes: secoesDisponiveis.filter(s => s.id !== sec.id).map(s => s.id) } } }; if (idx >= 0) novas[idx] = upd; else novas.push(upd); setPermissoes(novas); } else { toggleSecaoConfigurar(sec.id); } }}
                                    className="w-3.5 h-3.5 text-amber-600 rounded" />
                                  <div>
                                    <p className="font-medium text-gray-800">{sec.label}</p>
                                    <p className="text-[10px] text-gray-500">{sec.desc}</p>
                                  </div>
                                </label>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* ═══════════════ GERENCIAR PORTAL ═══════════════ */}
                {(() => {
                  const portalPerms = permissoesColaborador.permissoesGerais.gerenciarPortal || { visualizar: false, editar: false };
                  const ativo = portalPerms.visualizar || portalPerms.editar;
                  return (
                    <div className={`border rounded-lg overflow-hidden ${ativo ? 'border-teal-200 bg-teal-50/30' : 'border-gray-200'}`}>
                      <div className="flex items-center justify-between px-4 py-3">
                        <div className="flex items-center gap-3">
                          <span className="text-lg">🌐</span>
                          <div>
                            <p className="font-semibold text-sm text-gray-900">Gerenciar portais</p>
                            <p className="text-[11px] text-gray-500">
                              {portalPerms.editar ? 'Visualizar e editar' : portalPerms.visualizar ? 'Somente visualizar' : 'Bloqueado'}
                            </p>
                          </div>
                        </div>
                        <button onClick={() => setPermissaoExpandida(permissaoExpandida === 'portal' ? null : 'portal')} className="text-gray-400 hover:text-gray-600 text-xs px-1">
                          {permissaoExpandida === 'portal' ? '▲' : '▼'}
                        </button>
                      </div>
                      {permissaoExpandida === 'portal' && (
                        <div className="border-t border-gray-200 px-4 py-3 bg-white/80 space-y-2">
                          <label className="flex items-center justify-between cursor-pointer py-1.5 px-2 hover:bg-gray-50 rounded">
                            <div className="flex items-center gap-2">
                              <span className="text-sm">👁️</span>
                              <div>
                                <p className="text-xs font-medium text-gray-800">Visualizar portais</p>
                                <p className="text-[10px] text-gray-500">Ver configurações e submissões</p>
                              </div>
                            </div>
                            <div
                              onClick={() => togglePermissaoPortal('visualizar')}
                              className={`w-11 h-6 rounded-full transition-colors cursor-pointer relative ${
                                portalPerms.visualizar ? 'bg-green-500' : 'bg-gray-300'
                              }`}
                            >
                              <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform absolute top-0.5 ${
                                portalPerms.visualizar ? 'left-[22px]' : 'left-0.5'
                              }`}></div>
                            </div>
                          </label>
                          <label className="flex items-center justify-between cursor-pointer py-1.5 px-2 hover:bg-gray-50 rounded">
                            <div className="flex items-center gap-2">
                              <span className="text-sm">✏️</span>
                              <div>
                                <p className="text-xs font-medium text-gray-800">Editar portais</p>
                                <p className="text-[10px] text-gray-500">Criar, editar e excluir portais</p>
                              </div>
                            </div>
                            <div
                              onClick={() => togglePermissaoPortal('editar')}
                              className={`w-11 h-6 rounded-full transition-colors cursor-pointer relative ${
                                portalPerms.editar ? 'bg-green-500' : 'bg-gray-300'
                              }`}
                            >
                              <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform absolute top-0.5 ${
                                portalPerms.editar ? 'left-[22px]' : 'left-0.5'
                              }`}></div>
                            </div>
                          </label>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* ═══════════════ VISIBILIDADE DE CAMPOS ═══════════════ */}
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setPermissaoExpandida(permissaoExpandida === 'campos' ? null : 'campos')}
                    className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-lg">📋</span>
                      <div className="text-left">
                        <p className="font-semibold text-sm text-gray-900">Visibilidade de campos</p>
                        <p className="text-[11px] text-gray-500">
                          Definir quais campos pode visualizar e editar individualmente
                        </p>
                      </div>
                    </div>
                    <span className="text-gray-400 text-xs">{permissaoExpandida === 'campos' ? '▲' : '▼'}</span>
                  </button>
                  {permissaoExpandida === 'campos' && (
                    <div className="border-t border-gray-200 px-4 py-3 bg-white/80">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 text-[10px] text-gray-500 font-semibold uppercase tracking-wider px-2 pb-1 border-b">
                          <span className="flex-1">Campo</span>
                          <span className="w-14 text-center">Etapa</span>
                          <span className="w-16 text-center">Visualizar</span>
                          <span className="w-16 text-center">Editar</span>
                        </div>
                        {todosCampos.map(campo => {
                          const pc = permissoesColaborador.permissoesPorCampo[campo.id] || { visualizar: true, editar: true };
                          return (
                            <div key={campo.id} className="flex items-center gap-2 py-1.5 px-2 hover:bg-gray-50 rounded text-xs">
                              <span className="flex-1 truncate font-medium text-gray-800" title={campo.nome}>{campo.nome}</span>
                              <span className="w-14 text-center text-[10px] text-gray-400 truncate" title={campo.etapaNome}>{campo.etapaNome?.substring(0, 8)}</span>
                              <div className="w-16 flex justify-center">
                                <input type="checkbox" checked={pc.visualizar} onChange={() => togglePermissaoCampo(campo.id, 'visualizar')} className="w-4 h-4 text-blue-600 rounded cursor-pointer" />
                              </div>
                              <div className="w-16 flex justify-center">
                                <input type="checkbox" checked={pc.editar} onChange={() => togglePermissaoCampo(campo.id, 'editar')} className="w-4 h-4 text-blue-600 rounded cursor-pointer" />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex justify-between items-center shrink-0">
          <p className="text-xs text-gray-400">
            {colaboradorSelecionado && (
              <>Configurando: <span className="font-semibold text-gray-600">{colaboradores.find(c => c.id === colaboradorSelecionado)?.nome}</span></>
            )}
          </p>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-1.5 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">
              Cancelar
            </button>
            <button onClick={handleSalvar} className="px-4 py-1.5 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium">
              Salvar Permissões
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModalConfigPermissoes;
